/**
 * Project Cost Budget Excel import wizard — KEEP_SEPARATE from EST3 / Sales BOQ wizards.
 * Shows row-level validation so users can fix Excel before commit.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Radio,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  InboxOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation } from '@tanstack/react-query';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

export type BudgetImportPreviewRow = {
  rowNumber: number;
  row_type: string;
  wbs_code: string;
  description: string;
  cbs_code: string;
  cost_category: string;
  budgeted_quantity: number;
  uom: string;
  budgeted_unit_cost: number;
  budgeted_amount: number;
  notes: string;
  skipped: boolean;
  skip_reason?: string;
  errors: string[];
  wbs_will_create?: boolean;
};

export type BudgetImportErrorSummary = {
  issue: string;
  count: number;
  excel_rows: number[];
  values: string[];
  how_to_fix: string;
};

export type BudgetImportPreview = {
  preview_token?: string;
  sheet?: string;
  row_count: number;
  valid_count: number;
  skipped_count: number;
  error_count: number;
  mapped_total: number;
  ready: boolean;
  rows: BudgetImportPreviewRow[];
  error_summary?: BudgetImportErrorSummary[];
  wbs_to_create?: Array<{
    code: string;
    title: string;
    excel_rows: number[];
    will_create: boolean;
  }>;
  wbs_create_count?: number;
  reference?: {
    wbs_codes?: string[];
    wbs_labels?: Array<{ code: string; name: string }>;
    cbs_codes?: string[];
    cost_categories?: string[];
  };
  next_steps?: string;
};

export interface ProjectBudgetExcelImportWizardProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  revisionId?: string;
  readOnly?: boolean;
  onImported: (payload?: {
    revisionId?: string;
    lineCount?: number;
    wbsCreatedCount?: number;
  }) => void;
  onDownloadTemplate: () => Promise<void>;
}

type RowFilter = 'errors' | 'all' | 'valid' | 'skipped';

function downloadErrorsCsv(preview: BudgetImportPreview, fileName: string) {
  const bad = preview.rows.filter((r) => !r.skipped && r.errors.length);
  const header = [
    'excel_row',
    'wbs_code',
    'cbs_code',
    'cost_category',
    'description',
    'budgeted_amount',
    'errors',
  ];
  const lines = [
    header.join(','),
    ...bad.map((r) =>
      [
        r.rowNumber,
        csvCell(r.wbs_code),
        csvCell(r.cbs_code),
        csvCell(r.cost_category),
        csvCell(r.description),
        r.budgeted_amount,
        csvCell(r.errors.join('; ')),
      ].join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function csvCell(v: string) {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export const ProjectBudgetExcelImportWizard: React.FC<ProjectBudgetExcelImportWizardProps> = ({
  open,
  onClose,
  projectId,
  revisionId,
  readOnly,
  onImported,
  onDownloadTemplate,
}) => {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null);
  const [preview, setPreview] = useState<BudgetImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rowFilter, setRowFilter] = useState<RowFilter>('errors');
  const [result, setResult] = useState<{
    revisionId?: string;
    lineCount?: number;
    wbsCreatedCount?: number;
  } | null>(null);

  const reset = () => {
    setStep(0);
    setFile(null);
    setUploadFile(null);
    setPreview(null);
    setRowFilter('errors');
    setResult(null);
    setPreviewLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runPreview = async (f: File) => {
    setPreviewLoading(true);
    try {
      const data = (await projectService.previewBudgetImport(
        projectId,
        f
      )) as BudgetImportPreview;
      setPreview(data);
      setRowFilter(data.error_count > 0 ? 'errors' : 'all');
      setStep(1);
      if (data.error_count > 0) {
        message.warning(
          `${data.error_count} row error(s) — fix Excel using the review below, then re-upload`
        );
      } else {
        message.success(
          `Ready: ${data.valid_count} line(s) · ${data.skipped_count || 0} skipped · ${formatMoney(
            Number(data.mapped_total || 0)
          )}`
        );
      }
    } catch (e: any) {
      const apiMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Preview failed';
      message.error(typeof apiMsg === 'string' ? apiMsg : 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const commitM = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Select a file');
      if (!preview?.ready) throw new Error('Fix all errors before commit');
      return projectService.commitBudgetImport(projectId, file, {
        revisionId: revisionId || undefined,
        changeReason: 'Excel import',
      });
    },
    onSuccess: (data) => {
      const lineCount = Array.isArray(data?.lines) ? data.lines.length : preview?.valid_count;
      const revisionIdOut = data?.revision ? String((data.revision as any).id) : revisionId;
      const wbsCreatedCount = Number((data as any)?.wbs_created_count || 0);
      setResult({
        revisionId: revisionIdOut,
        lineCount,
        wbsCreatedCount,
      });
      setStep(2);
      message.success(
        wbsCreatedCount
          ? `Import committed · created ${wbsCreatedCount} WBS node(s)`
          : 'Import committed'
      );
      onImported({ revisionId: revisionIdOut, lineCount, wbsCreatedCount });
    },
    onError: (e: Error) => message.error(e.message || 'Import failed'),
  });

  const filteredRows = useMemo(() => {
    const rows = preview?.rows || [];
    if (rowFilter === 'errors') return rows.filter((r) => !r.skipped && r.errors.length > 0);
    if (rowFilter === 'valid') return rows.filter((r) => !r.skipped && r.errors.length === 0);
    if (rowFilter === 'skipped') return rows.filter((r) => r.skipped);
    return rows;
  }, [preview, rowFilter]);

  return (
    <Modal
      title="Import Project Cost Budget (Excel)"
      open={open}
      onCancel={handleClose}
      width={1080}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleClose}>{step === 2 ? 'Close' : 'Cancel'}</Button>
          {step === 1 && (
            <>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setStep(0);
                  setPreview(null);
                }}
              >
                Fix Excel & re-upload
              </Button>
              <Button
                type="primary"
                disabled={readOnly || !preview?.ready || !file}
                loading={commitM.isPending}
                onClick={() => commitM.mutate()}
              >
                Commit {preview?.valid_count || 0} valid line(s)
              </Button>
            </>
          )}
        </Space>
      }
    >
      {readOnly ? (
        <Alert type="warning" showIcon message="This revision is not editable — import is blocked." />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Steps
            size="small"
            current={step}
            items={[
              { title: 'Upload' },
              { title: 'Review & fix' },
              { title: 'Done' },
            ]}
          />

          {step === 0 && (
            <>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Use the project workbook (Budget_Lines). Missing WBS codes are created on Project
                WBS at commit. CBS codes must match the catalogue when provided. SECTION / TOTAL
                rows are skipped automatically.
                {!revisionId
                  ? ' No editable revision is selected — commit will create a new Draft.'
                  : ''}
              </Paragraph>
              <Space wrap>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    try {
                      await onDownloadTemplate();
                    } catch (e: any) {
                      message.error(e?.message || 'Template download failed');
                    }
                  }}
                >
                  Download import workbook
                </Button>
              </Space>
              <Dragger
                multiple={false}
                maxCount={1}
                accept=".xlsx,.xls"
                disabled={previewLoading}
                fileList={uploadFile ? [uploadFile] : []}
                beforeUpload={async (f) => {
                  setFile(f);
                  setUploadFile({ uid: f.name, name: f.name, status: 'done' });
                  await runPreview(f);
                  return false;
                }}
                onRemove={() => {
                  setFile(null);
                  setUploadFile(null);
                  setPreview(null);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {previewLoading ? 'Validating workbook…' : 'Drop Excel here to validate'}
                </p>
                <p className="ant-upload-hint">.xlsx / .xls — preview runs before any commit</p>
              </Dragger>
            </>
          )}

          {step === 1 && preview && (
            <>
              <Alert
                type={preview.ready ? 'success' : preview.error_count ? 'error' : 'warning'}
                showIcon
                message={
                  preview.ready
                    ? `Ready to commit · ${preview.valid_count} lines · ${formatMoney(
                        Number(preview.mapped_total || 0)
                      )}`
                    : preview.error_count
                      ? `${preview.error_count} error(s) block commit — fix Excel, then re-upload`
                      : 'No importable LINE rows found'
                }
                description={
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Text>
                      Sheet: <Text code>{preview.sheet || '—'}</Text> · Valid{' '}
                      {preview.valid_count} · Skipped {preview.skipped_count || 0} · Errors{' '}
                      {preview.error_count}
                    </Text>
                    {preview.next_steps && <Text type="secondary">{preview.next_steps}</Text>}
                  </Space>
                }
              />

              {(preview.wbs_create_count || 0) > 0 && (
                <Alert
                  type="info"
                  showIcon
                  message={`Will create ${preview.wbs_create_count} Project WBS node(s) on commit`}
                  description={
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Missing codes from Excel (and inferred parents) are added to the primary
                        Project WBS automatically.
                      </Text>
                      <div>
                        {(preview.wbs_to_create || []).slice(0, 30).map((w) => (
                          <Tag key={w.code} style={{ marginBottom: 4 }}>
                            {w.code}
                            {w.title && w.title !== w.code ? ` — ${w.title}` : ''}
                          </Tag>
                        ))}
                        {(preview.wbs_to_create || []).length > 30
                          ? `… +${(preview.wbs_to_create || []).length - 30} more`
                          : null}
                      </div>
                    </Space>
                  }
                />
              )}

              {(preview.error_summary || []).length > 0 && (
                <CardLikeIssues summary={preview.error_summary!} reference={preview.reference} />
              )}

              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Radio.Group
                  size="small"
                  value={rowFilter}
                  onChange={(e) => setRowFilter(e.target.value)}
                  optionType="button"
                  options={[
                    {
                      label: `Errors (${preview.error_count})`,
                      value: 'errors',
                    },
                    {
                      label: `Valid (${preview.valid_count})`,
                      value: 'valid',
                    },
                    {
                      label: `Skipped (${preview.skipped_count || 0})`,
                      value: 'skipped',
                    },
                    {
                      label: `All (${preview.row_count})`,
                      value: 'all',
                    },
                  ]}
                />
                <Space>
                  {preview.error_count > 0 && (
                    <Button
                      size="small"
                      icon={<FileExcelOutlined />}
                      onClick={() =>
                        downloadErrorsCsv(
                          preview,
                          `budget-import-errors-${projectId.slice(0, 8)}.csv`
                        )
                      }
                    >
                      Download error list (CSV)
                    </Button>
                  )}
                </Space>
              </Space>

              <Table
                size="small"
                rowKey={(r) => `${r.rowNumber}-${r.wbs_code}-${r.cost_category}-${r.description}`}
                dataSource={filteredRows}
                pagination={{ pageSize: 15, showSizeChanger: true }}
                scroll={{ x: 1100, y: 360 }}
                columns={[
                  { title: 'Excel row', dataIndex: 'rowNumber', width: 88, fixed: 'left' },
                  {
                    title: 'Status',
                    width: 120,
                    fixed: 'left',
                    render: (_: unknown, r: BudgetImportPreviewRow) => {
                      if (r.skipped) return <Tag>skipped</Tag>;
                      if (r.errors.length) return <Tag color="error">error</Tag>;
                      if (r.wbs_will_create) return <Tag color="blue">WBS+OK</Tag>;
                      return <Tag color="success">valid</Tag>;
                    },
                  },
                  {
                    title: 'WBS',
                    dataIndex: 'wbs_code',
                    width: 130,
                    render: (v: string, r: BudgetImportPreviewRow) => (
                      <Space size={4}>
                        <span>{v || '—'}</span>
                        {r.wbs_will_create ? <Tag color="blue">new</Tag> : null}
                      </Space>
                    ),
                  },
                  { title: 'CBS', dataIndex: 'cbs_code', width: 100 },
                  { title: 'Category', dataIndex: 'cost_category', width: 110 },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    ellipsis: true,
                    width: 200,
                  },
                  {
                    title: 'Qty',
                    dataIndex: 'budgeted_quantity',
                    width: 72,
                    align: 'right' as const,
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'budgeted_amount',
                    width: 110,
                    align: 'right' as const,
                    render: (v: number) => formatMoney(Number(v || 0)),
                  },
                  {
                    title: 'What is wrong / note',
                    width: 320,
                    render: (_: unknown, r: BudgetImportPreviewRow) => {
                      if (r.skipped) {
                        return <Text type="secondary">{r.skip_reason || 'Skipped'}</Text>;
                      }
                      if (!r.errors.length) return <Text type="secondary">OK</Text>;
                      return (
                        <Space direction="vertical" size={2}>
                          {r.errors.map((err) => (
                            <Tag key={err} color="red" style={{ whiteSpace: 'normal', maxWidth: 300 }}>
                              {err}
                            </Tag>
                          ))}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </>
          )}

          {step === 2 && (
            <Alert
              type="success"
              showIcon
              message={`Imported ${result?.lineCount ?? preview?.valid_count ?? 0} budget line(s)`}
              description={
                <Space direction="vertical" size={4}>
                  {(result?.wbsCreatedCount || 0) > 0 && (
                    <Text>Created {result?.wbsCreatedCount} Project WBS node(s) from Excel codes.</Text>
                  )}
                  <Text type="secondary">
                    {result?.revisionId
                      ? `Lines written to revision ${result.revisionId}. Review in the workbench, then submit for approval.`
                      : 'Import complete. Review lines in the workbench, then submit for approval.'}
                  </Text>
                </Space>
              }
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

function CardLikeIssues({
  summary,
  reference,
}: {
  summary: BudgetImportErrorSummary[];
  reference?: BudgetImportPreview['reference'];
}) {
  return (
    <div
      style={{
        border: '1px solid #ffccc7',
        background: '#fff2f0',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        How to correct ({summary.length} issue type{summary.length === 1 ? '' : 's'})
      </Text>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {summary.map((item) => (
          <div key={`${item.issue}-${item.count}`}>
            <Space wrap size={4}>
              <Tag color="red">
                {item.count}× {item.issue}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Excel rows: {item.excel_rows.slice(0, 12).join(', ')}
                {item.excel_rows.length > 12 ? ` +${item.excel_rows.length - 12} more` : ''}
              </Text>
            </Space>
            <div>
              <Text style={{ fontSize: 13 }}>{item.how_to_fix}</Text>
            </div>
            {item.values.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Values in file:{' '}
                  {item.values.slice(0, 15).map((v) => (
                    <Text code key={v} style={{ marginRight: 4 }}>
                      {v || '(blank)'}
                    </Text>
                  ))}
                  {item.values.length > 15 ? '…' : null}
                </Text>
              </div>
            )}
          </div>
        ))}
      </Space>
      {!!reference?.wbs_codes?.length && summary.some((s) => /WBS/i.test(s.issue)) && (
        <div style={{ marginTop: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Project WBS codes (use exactly):{' '}
            {reference.wbs_codes.slice(0, 25).map((c) => (
              <Text code key={c} style={{ marginRight: 4 }}>
                {c}
              </Text>
            ))}
            {reference.wbs_codes.length > 25
              ? `… +${reference.wbs_codes.length - 25} in Project_WBS sheet`
              : null}
          </Text>
        </div>
      )}
      {!!reference?.cbs_codes?.length && summary.some((s) => /CBS/i.test(s.issue)) && (
        <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sample CBS codes:{' '}
            {reference.cbs_codes.slice(0, 20).map((c) => (
              <Text code key={c} style={{ marginRight: 4 }}>
                {c}
              </Text>
            ))}
            {reference.cbs_codes.length > 20 ? '…' : null}
          </Text>
        </div>
      )}
      {!!reference?.cost_categories?.length &&
        summary.some((s) => /categor/i.test(s.issue)) && (
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Allowed categories:{' '}
              {reference.cost_categories.map((c) => (
                <Text code key={c} style={{ marginRight: 4 }}>
                  {c}
                </Text>
              ))}
            </Text>
          </div>
        )}
    </div>
  );
}
